import React, {useEffect, useState} from 'react'
import {Link, useLocation, useParams} from 'react-router-dom'
import {useReduxAuth} from '../../../auth'
import TimerComponent from './TimerComponent'
import Loader from '../Loader'
import {FaQuestionCircle} from 'react-icons/fa'
import PhoneLink from '../common/PhoneLink'
import {RoleGuard} from '../permissions/RoleGuard'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import ManualBPEntryForm from '../forms/ManualBPEntryForm'
import {Modal} from 'react-bootstrap'
import {FiPlus} from 'react-icons/fi'
import {getInitials} from '../common/CardUserInfo'
import PatientDetailBadges from '../common/PatientDetailBadges'
import {fetchSignedUrl} from '../../helper/helperFunction'
import {toast} from 'react-toastify'
import {isPatientActive} from '../../../../utitlities/patientStatus'

interface LinkItem {
  title: string
  path: string
  label: string
  allowedRoles: number[]
  icon?: React.ReactNode
}

interface ProfileDetailHeaderProps {
  user?: any
  uuid: string
  isLoading?: boolean
  links?: LinkItem[]
  /** Called when a manual BP entry successfully saves */
  onManualEntrySuccess?: () => void
}

export const ProfileDetailHeader: React.FC<ProfileDetailHeaderProps> = ({
  user,
  uuid,
  links,
  isLoading,
  onManualEntrySuccess,
}) => {
  const active = isPatientActive(user)

  const env = process.env.REACT_APP_ENV
  const location = useLocation()
  const {currentUser} = useReduxAuth()
  const notifyError = (msg: string) => toast.error(msg)

  // Combine first and last names into a single string
  const fullName = user?.full_name
  const initials = fullName ? getInitials(fullName) : 'NN'

  const {patientId} = useParams<{patientId: string}>()
  const doctorId = currentUser?.id

  // State for manual‐entry modal
  const [showManualModal, setShowManualModal] = useState(false)
  const [profileImage, setProfileImage] = useState<any>('')

  const handleManualSuccess = () => {
    setShowManualModal(false)
    onManualEntrySuccess?.()
  }

  // Fetch signed URL whenever `imageUrl` changes
  useEffect(() => {
    const fetch = async () => {
      if (!user?.profile_image) return

      try {
        const preSignedURL = await fetchSignedUrl(user.profile_image)
        setProfileImage(preSignedURL)
      } catch (err) {
        // toast.error('Error occurred while fetching the image')
        setProfileImage(null)
      }
    }

    fetch()
  }, [user?.profile_image])

  if (isLoading) {
    return <Loader color='primary' size='lg' message='' />
  }

  return (
    <div className='mb-3 mb-xl-10 modal-lg'>
      <div>
        {/* Top Row */}
        <div className='d-flex flex-row flex-wrap align-items-center justify-content-between gap-3 mb-4'>
          {/* Avatar + Name */}
          <div className='d-flex align-items-center'>
            {!user?.profile_image ? (
              <div className='d-flex align-items-center bg-primary text-white justify-content-center fs-2 fw-bolder me-3 rounded-circle profile-style avater-style'>
                {initials}
              </div>
            ) : (
              <img
                src={profileImage}
                alt='Profile'
                className='me-2'
                style={{
                  borderRadius: '50%',
                  objectFit: 'cover',
                  width: '60px',
                  height: '60px',
                }}
              />
            )}

            <div className='d-flex flex-column'>
              <div className='d-flex gap-4'>
                <h1 className='fs-2 fw-bold text-dark mb-1'>{user?.full_name}</h1>
                {user?.phone_number && (
                  <div className='d-flex align-items-center text-muted mb-1'>
                    <PhoneLink phone={user.phone_number} />
                  </div>
                )}
              </div>

              {/* Status badge + Readings + Care Minutes Badges */}
              {patientId && (
                <RoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <div className='d-flex align-items-center gap-2'>
                    <span className={`badge badge-lg badge-pill bg-${user?.status === 1 ? 'success' : 'danger'}`}>{user?.status === 1 ? 'Active' : 'Inactive'}</span>
                    <PatientDetailBadges patientUUID={patientId!} />
                  </div>
                </RoleGuard>
              )}
            </div>
          </div>

          {/* Timer + Manual BP‐Entry Button */}
          <RoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
            {location.pathname.includes('patient-details') && (
              <div className='d-flex flex-column flex-sm-row align-items-start gap-3'>
                {/* Timer button */}
                <TimerComponent
                  patientData={user}
                  onEntryAdded={onManualEntrySuccess || (() => {})}
                />

                {['local', 'development', 'staging'].includes(env!) && (
                  <button
                    className='btn-with-icon'
                    onClick={() =>
                      active ? setShowManualModal(true) : notifyError('Patient is inactive')
                    }
                  >
                    <div className='plus-icon-box'>
                      <FiPlus />
                    </div>
                    Reading
                  </button>
                )}

                {/* Manual Reading Entry Modal */}
                <Modal
                  show={showManualModal}
                  onHide={() => setShowManualModal(false)}
                  centered
                  size='lg'
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Add Manual Reading</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <ManualBPEntryForm
                      patientUUID={patientId!}
                      doctorId={String(doctorId)}
                      onSuccess={handleManualSuccess}
                    />
                  </Modal.Body>
                </Modal>
              </div>
            )}
          </RoleGuard>
        </div>

        {/* Navigation Links */}
        {links && (
          <div className='d-flex overflow-auto'>
            <ul className='nav nav-stretch nav-line-tabs border-transparent fs-5 fw-bolder flex-wrap'>
              {links
                .filter((link) => currentUser && link.allowedRoles.includes(currentUser.role_id))
                .map((link, idx) => {
                  const isActive = location.pathname.startsWith(link.path)
                  const primaryColor = '#6f42c1'
                  const lightBgColor = '#e5d8ff'
                  return (
                    <li key={idx} className='nav-item'>
                      <Link
                        to={link.path}
                        className={
                          'nav-link d-flex align-items-center gap-2 text-capitalize px-6 py-2 my-2 ' +
                          (isActive ? 'active' : '')
                        }
                        style={{
                          border: `2px solid ${isActive ? primaryColor : '#ddd'}`,
                          borderRadius: '100px',
                          transition: 'background-color 0.2s ease',
                          backgroundColor: isActive ? lightBgColor : '',
                          color: isActive ? primaryColor : '',
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = '#f8f9fa'
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = ''
                        }}
                      >
                        {link.icon || <FaQuestionCircle />}
                        <span className='fs-6'>{link.title}</span>
                      </Link>
                    </li>
                  )
                })}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfileDetailHeader
